/**
 * PowerSync-based React hooks для web-приложения.
 * 
 * Этап 2: Чтение данных из PowerSync (локальный SQLite через wa-sqlite).
 * Использует getPowerSync() напрямую с useEffect + watch() для реактивных обновлений.
 * 
 * Не требует PowerSyncContext.Provider — работает через синглтон getPowerSync().
 */

import { useState, useEffect, useMemo } from 'react';
import { getPowerSync } from '../platform/init';

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 🔧 Утилита: выполняет SQL запрос и подписывается на обновления через watch
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function usePowerSyncQuery<T = any>(sql: string, params: any[] = [], tables?: string[]): { data: T[]; isLoading: boolean } {
  const [data, setData] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Стабильный ключ для params
  const paramsKey = JSON.stringify(params);

  useEffect(() => {
    let cancelled = false;
    let abortController: AbortController | null = null;

    const startWatching = async () => {
      // Ждём пока PowerSync будет готов
      const waitForDb = () => new Promise<any>((resolve) => {
        const check = () => {
          const db = getPowerSync();
          if (db) {
            resolve(db);
          } else {
            setTimeout(check, 500);
          }
        };
        check();
      });

      const db = await waitForDb();
      if (cancelled) return;

      // Первоначальная загрузка
      try {
        const result = await db.getAll(sql, params) as T[];
        if (!cancelled) {
          setData(result);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('[PowerSync Web] Query error:', error);
        if (!cancelled) setIsLoading(false);
      }

      // Подписка на изменения через watch
      try {
        abortController = new AbortController();
        const watchOptions: any = { signal: abortController.signal };
        if (tables) watchOptions.tables = tables;

        for await (const result of db.watch(sql, params, watchOptions)) {
          if (cancelled) break;
          // watch может вернуть результат в разных форматах
          const rows = (result as any)?.rows?._array || (result as any)?.rows || result;
          if (Array.isArray(rows)) {
            setData(rows);
          }
        }
      } catch (err: any) {
        if (err?.name !== 'AbortError' && !cancelled) {
          console.error('[PowerSync Web] Watch error:', err);
        }
      }
    };

    startWatching();

    return () => {
      cancelled = true;
      abortController?.abort();
    };
  }, [sql, paramsKey]);

  return { data, isLoading };
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 📊 Транзакции
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export function useWebPowerSyncTransactions() {
  const { data: expenses, isLoading: el } = usePowerSyncQuery(
    `SELECT id, 'expense' as type, 'manual' as source,
       title, amount, payment_date as date, category, category_id,
       description, payment_method_id, is_internal_transfer,
       NULL as merchant_name
     FROM expenses ORDER BY payment_date DESC`,
    [], ['expenses']
  );

  const { data: income, isLoading: il } = usePowerSyncQuery(
    `SELECT id, 'income' as type, 'manual' as source,
       title, amount, transaction_date as date, NULL as category, NULL as category_id,
       description, payment_method_id, is_internal_transfer,
       NULL as merchant_name
     FROM income_transactions ORDER BY transaction_date DESC`,
    [], ['income_transactions']
  );

  const { data: plaid, isLoading: pl } = usePowerSyncQuery(
    `SELECT id,
       CASE WHEN CAST(amount AS REAL) < 0 THEN 'income' ELSE 'expense' END as type,
       'plaid' as source,
       COALESCE(merchant_name, name) as title,
       ABS(CAST(amount AS REAL)) as amount,
       date, category, category_id,
       NULL as description, NULL as payment_method_id, is_internal_transfer,
       merchant_name
     FROM plaid_transactions ORDER BY date DESC`,
    [], ['plaid_transactions']
  );

  const transactions = useMemo(() => {
    const all = [
      ...(expenses || []).map((e: any) => ({ ...e, amount: Number(e.amount), is_internal_transfer: !!e.is_internal_transfer })),
      ...(income || []).map((i: any) => ({ ...i, amount: Number(i.amount), is_internal_transfer: !!i.is_internal_transfer })),
      ...(plaid || []).map((p: any) => ({ ...p, amount: Number(p.amount), is_internal_transfer: !!p.is_internal_transfer })),
    ];
    all.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    return all;
  }, [expenses, income, plaid]);

  return { transactions, isLoading: el || il || pl };
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 📁 Категории
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export function useWebPowerSyncCategories() {
  const { data: rawCategories, isLoading } = usePowerSyncQuery(
    `SELECT * FROM categories ORDER BY is_system DESC, sort_order ASC, name ASC`,
    [], ['categories']
  );

  const categories = useMemo(() => {
    return (rawCategories || []).map((c: any) => ({
      ...c,
      is_system: !!c.is_system,
    }));
  }, [rawCategories]);

  return { categories, isLoading };
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 🎯 Цели
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export function useWebPowerSyncGoals() {
  const { data: rawGoals, isLoading } = usePowerSyncQuery(
    `SELECT * FROM goals ORDER BY created_at DESC`,
    [], ['goals']
  );

  const goals = useMemo(() => {
    return (rawGoals || []).map((g: any) => ({
      ...g,
      target_amount: Number(g.target_amount) || 0,
      current_amount: Number(g.current_amount) || 0,
      deposit_limit: g.deposit_limit != null ? Number(g.deposit_limit) : null,
    }));
  }, [rawGoals]);

  return { goals, isLoading };
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 📏 Лимиты
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export function useWebPowerSyncLimits() {
  const { data: rawLimits, isLoading } = usePowerSyncQuery(
    `SELECT * FROM category_limits ORDER BY category_name ASC`,
    [], ['category_limits']
  );

  const now = new Date();
  const startOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const endOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()).padStart(2, '0')}`;

  const { data: expensesByCategory } = usePowerSyncQuery(
    `SELECT category_id, category, SUM(amount) as total
     FROM expenses
     WHERE payment_date >= ? AND payment_date <= ? AND is_internal_transfer = 0
     GROUP BY COALESCE(category_id, category)`,
    [startOfMonth, endOfMonth], ['expenses']
  );

  const limits = useMemo(() => {
    const expenseMap: Record<string, number> = {};
    for (const e of (expensesByCategory || [])) {
      const key = (e as any).category_id ? String((e as any).category_id) : (e as any).category || '';
      expenseMap[key] = (expenseMap[key] || 0) + Number((e as any).total);
    }

    return (rawLimits || []).map((l: any) => {
      const limitAmount = Number(l.limit_amount) || 0;
      const key = l.category_id ? String(l.category_id) : l.category_name || '';
      const spent = expenseMap[key] || 0;
      return {
        ...l,
        limit_amount: limitAmount,
        spent,
        remaining: Math.max(0, limitAmount - spent),
        percentage: limitAmount > 0 ? Math.min(100, (spent / limitAmount) * 100) : 0,
      };
    });
  }, [rawLimits, expensesByCategory]);

  return { limits, isLoading };
}
